import type {InvestmentHoldingsQuery} from '@transactions/graphql';

export type Holding = InvestmentHoldingsQuery['investmentHoldings'][number];

/**
 * An amount summed over holdings: the known part, how many were known,
 * whether that's all, and whether what's missing can only add to it (so
 * the known part is a floor: every unknown is a long position's).
 */
export type Sum = {cents: number; known: number; complete: boolean; floor: boolean};

/** A row: one security's holdings summed, or one account's holding of it. One currency. */
export type HoldingRow = {
  key: string;
  name: string;
  ticker: string | null;
  currency: string;
  /** Null when the row mixes securities (cash). */
  quantity: number | null;
  /** The oldest day its holdings, or the quotes valuing them, are as of. */
  asOf: string;
  /** Null unless every holding has a quote and they agree. */
  price: string | null;
  value: Sum;
  /** Null when any holding's basis (or value) is unknown; cash has none. */
  costBasis: number | null;
  unrealized: number | null;
  cash: boolean;
  holdings: Holding[];
};

/**
 * Totals in one currency, over investments (not cash): basis over every
 * known basis (incomplete when one isn't); unrealized over the gains known.
 */
export type Total = {
  currency: string;
  value: Sum;
  costBasis: Sum;
  unrealized: Sum;
};

export type HoldingGroup = {
  key: string;
  name: string;
  totals: Total[];
  rows: HoldingRow[];
};

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const sumOf = (holdings: Holding[], pick: (holding: Holding) => number | null | undefined): Sum => {
  const unknown = holdings.filter((holding) => pick(holding) == null);
  return {
    cents: sum(holdings.map((holding) => pick(holding) ?? 0)),
    known: holdings.length - unknown.length,
    complete: unknown.length === 0,
    floor: unknown.every((holding) => holding.quantity >= 0),
  };
};

const allKnown = (values: Array<number | null | undefined>): number | null =>
  values.some((value) => value == null) ? null : sum(values.map((value) => value ?? 0));

/** Holdings of one asset (or of cash), in one currency, summed into one row. */
export const rowOf = (key: string, name: string, holdings: Holding[]): HoldingRow => {
  const [first] = holdings;
  const cash = holdings.every((holding) => holding.cash);
  const oneSecurity = !cash;
  // A quote only when every holding has one and they agree.
  const quoted = holdings.every((holding) => holding.price != null);
  const quotes = new Set(holdings.map((holding) => Number(holding.price)));
  return {
    key,
    name,
    ticker: oneSecurity ? (holdings.find((holding) => holding.security.ticker != null)?.security.ticker ?? null) : null,
    currency: first.currency,
    asOf: holdings.flatMap((holding) => [holding.asOf, holding.priceDate ?? holding.asOf]).sort()[0],
    quantity: oneSecurity && !cash ? sum(holdings.map((holding) => holding.quantity)) : null,
    price:
      oneSecurity && !cash && quoted && quotes.size === 1
        ? (holdings.find((holding) => holding.price != null)?.price ?? null)
        : null,
    value: sumOf(holdings, (holding) => holding.value),
    costBasis: cash ? null : allKnown(holdings.map((holding) => holding.costBasis)),
    unrealized: cash ? null : allKnown(holdings.map((holding) => holding.unrealized)),
    cash,
    holdings,
  };
};

const byValue = (a: {value: Sum}, b: {value: Sum}) => b.value.cents - a.value.cents;

/** The account (or company stock issuer) a holding is in. */
export const accountKeyOf = (holding: Holding): string => holding.accountId ?? holding.issuerId ?? '';

/** A security's stable identifiers: its identity key, CUSIP and ISIN. */
const identifiersOf = ({security}: Holding): string[] => [
  security.identityKey,
  ...(security.cusip != null ? [`cusip:${security.cusip}`] : []),
  ...(security.isin != null ? [`isin:${security.isin}`] : []),
];

/**
 * Which asset each holding is: securities sharing any stable identifier
 * (a CUSIP or an ISIN, though one record lacks the other) are one asset.
 */
export const assetsOf = (holdings: Holding[]): ((holding: Holding) => string) => {
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    const up = parent.get(key) ?? key;
    if (up === key) return key;
    const root = find(up);
    parent.set(key, root);
    return root;
  };
  for (const holding of holdings) {
    const [first, ...rest] = identifiersOf(holding);
    for (const key of rest) parent.set(find(key), find(first));
  }
  return (holding) => find(holding.security.identityKey);
};

/**
 * One row per security and currency (grouped by identity across
 * accounts), cash last as one row per currency.
 */
export const rowsBySecurity = (
  holdings: Holding[],
  // Identity over a larger set, when these are part of one: a bridge
  // between two records may be a holding outside this subset.
  assetOf: (holding: Holding) => string = assetsOf(holdings),
): HoldingRow[] => {
  const groups = new Map<string, Holding[]>();
  for (const holding of holdings) {
    const key = `${holding.cash ? 'cash' : assetOf(holding)} ${holding.currency}`;
    groups.set(key, [...(groups.get(key) ?? []), holding]);
  }
  const rows = [...groups.entries()].map(([key, members]) =>
    rowOf(key, members[0].cash ? 'Cash' : members[0].security.name, members),
  );
  return [
    ...rows.filter((row) => !row.cash).sort(byValue),
    ...rows.filter((row) => row.cash).sort(byValue),
  ];
};

/** Totals per currency: USD first, then by value. */
export const totalsOf = (holdings: Holding[]): Total[] => {
  const byCurrency = new Map<string, Holding[]>();
  for (const holding of holdings) {
    byCurrency.set(holding.currency, [...(byCurrency.get(holding.currency) ?? []), holding]);
  }
  return [...byCurrency.entries()]
    .map(([currency, members]) => {
      const invested = members.filter((holding) => !holding.cash);
      return {
        currency,
        value: sumOf(members, (holding) => holding.value),
        costBasis: sumOf(invested, (holding) => holding.costBasis),
        unrealized: sumOf(invested, (holding) => holding.unrealized),
      };
    })
    .sort(
      (a, b) =>
        Number(b.currency === 'USD') - Number(a.currency === 'USD') || b.value.cents - a.value.cents,
    );
};

/** One group per account (or company stock issuer), its holdings as rows. */
export const groupsByAccount = (
  holdings: Holding[],
  accountName: (holding: Holding) => string,
  // As for rowsBySecurity: identity over a larger set these are part of.
  assetOf: (holding: Holding) => string = assetsOf(holdings),
): HoldingGroup[] => {
  const groups = new Map<string, Holding[]>();
  for (const holding of holdings) {
    const key = accountKeyOf(holding);
    groups.set(key, [...(groups.get(key) ?? []), holding]);
  }
  return [...groups.entries()]
    .map(([key, members]) => ({
      key,
      name: accountName(members[0]),
      totals: totalsOf(members),
      rows: rowsBySecurity(members, assetOf),
    }))
    .sort((a, b) => (b.totals[0]?.value.cents ?? 0) - (a.totals[0]?.value.cents ?? 0));
};

/** How the rows are ordered: value is the default. */
export type HoldingSort = 'value' | 'unrealized' | 'return' | 'name';

/** A row's return on its cost basis; null without both, or on no basis (options). */
export const returnOf = (row: HoldingRow): number | null =>
  row.unrealized != null && row.costBasis != null && row.costBasis > 0 ? row.unrealized / row.costBasis : null;

/**
 * Rows in the chosen order, cash last whatever it is (it has no gain, and
 * reads as the remainder). Unknown figures sort after known ones; ties by
 * value.
 */
export const sortRows = (rows: HoldingRow[], sort: HoldingSort): HoldingRow[] => {
  const descending = (pick: (row: HoldingRow) => number | null) => (a: HoldingRow, b: HoldingRow) => {
    const [x, y] = [pick(a), pick(b)];
    if (x == null || y == null) return Number(x == null) - Number(y == null) || byValue(a, b);
    return y - x || byValue(a, b);
  };
  const compare =
    sort === 'name'
      ? (a: HoldingRow, b: HoldingRow) => a.name.localeCompare(b.name) || byValue(a, b)
      : sort === 'unrealized'
        ? descending((row) => row.unrealized)
        : sort === 'return'
          ? descending(returnOf)
          : byValue;
  return [...rows.filter((row) => !row.cash).sort(compare), ...rows.filter((row) => row.cash).sort(byValue)];
};

/**
 * The holdings a filter keeps: in the chosen accounts (none chosen: all),
 * and matching the search in the account's name, or in the security's name
 * or ticker. A security match keeps every record of the asset, since one
 * institution's record may lack the ticker or name another's matched.
 */
export const filterHoldings = (
  holdings: Holding[],
  filter: {accounts: string[]; search: string},
  accountName: (holding: Holding) => string,
  assetOf: (holding: Holding) => string = assetsOf(holdings),
): Holding[] => {
  const query = filter.search.trim().toLowerCase();
  const inAccounts = holdings.filter(
    (holding) => filter.accounts.length === 0 || filter.accounts.includes(accountKeyOf(holding)),
  );
  if (query === '') return inAccounts;
  const matches = (text: string | null | undefined) => text != null && text.toLowerCase().includes(query);
  const assets = new Set(
    inAccounts.filter((holding) => matches(holding.security.name) || matches(holding.security.ticker)).map(assetOf),
  );
  return inAccounts.filter((holding) => assets.has(assetOf(holding)) || matches(accountName(holding)));
};

/** Tax treatments in the order they're shown, with their labels. */
export const TAX_TREATMENTS: Array<{key: string; label: string}> = [
  {key: 'taxable', label: 'Taxable'},
  {key: 'tax_deferred', label: 'Tax-deferred'},
  {key: 'tax_free', label: 'Tax-free'},
  {key: 'unknown', label: 'Unknown'},
];

/**
 * Totals by tax treatment, in each currency: what's held, at what cost, and
 * its unrealized gain, taxable apart from what's sheltered. One row per
 * treatment and currency held, in TAX_TREATMENTS order.
 */
export const totalsByTreatment = (holdings: Holding[]): Array<Total & {treatment: string; label: string}> =>
  TAX_TREATMENTS.flatMap(({key, label}) =>
    totalsOf(holdings.filter((holding) => (holding.taxTreatment ?? 'unknown') === key)).map((total) => ({
      ...total,
      treatment: key,
      label,
    })),
  );
